import React from "react";
import Nullable from "@data-force/app/interfaces/nullable";
import {UserManager} from "oidc-client";
import {connect} from "react-redux";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {Button, Layout, Menu} from "antd";
import {
    ContainerOutlined, InfoCircleOutlined, LogoutOutlined, RightOutlined, LeftOutlined, ForwardOutlined,
} from "@ant-design/icons";
import StoreState from "../../interfaces/store/storeState";
import {SetContentTypeAction, ToggleSidebarAction} from "../../interfaces/store/actions/layout";
import {setContentType, toggleSidebar} from "../../store/actions/layout";
import logo from "../../assets/Sidebar/logo-prod.svg";
import typefaceLogo from "../../assets/Sidebar/typeface-logo-prod.svg";
import logoDev from "../../assets/Sidebar/logo-dev.svg";
import typefaceLogoDev from "../../assets/Sidebar/typeface-logo-dev.svg";
import logoStg1 from "../../assets/Sidebar/logo-stg1.svg";
import typefaceLogoStg1 from "../../assets/Sidebar/typeface-logo-stg1.svg";
import logoStg2 from "../../assets/Sidebar/logo-stg2.svg";
import typefaceLogoStg2 from "../../assets/Sidebar/typeface-logo-stg2.svg";
import {ContentType} from "../../interfaces/store/actions/payload/layout";
import {ENTER_KEY, SPACE_KEY} from "../../utils/constants";
import {Environment} from "../../enums/enums";
import "./Sidebar.scss";

interface DispatchProps {
    toggle(): ToggleSidebarAction

    setContentType(payload: ContentType): SetContentTypeAction
}

interface StateProps {
    collapsed: boolean
    userManager: Nullable<UserManager>
    translate: TranslateFunction
    contentType: ContentType
    env: Environment
}

interface OwnProps {
    focusMainContent: Function
}

type Props = StateProps & DispatchProps & OwnProps;

class Sidebar extends React.Component<Props> {
    private static readonly translationPath: string = "components.Sidebar.Sidebar";

    private static readonly tasksKey: string = "tasks";

    private static readonly instructionsKey: string = "instructions";

    private static readonly skipLinkKey: string = "skipLink";

    constructor(props: Props) {
        super(props);

        this.logout = this.logout.bind(this);
        this.displayTasks = this.displayTasks.bind(this);
        this.displayInstructions = this.displayInstructions.bind(this);
        this.getSelectedKey = this.getSelectedKey.bind(this);
        this.onDisplayTasks = this.onDisplayTasks.bind(this);
        this.onDisplayInstructions = this.onDisplayInstructions.bind(this);
        this.onLogout = this.onLogout.bind(this);
        this.renderTrigger = this.renderTrigger.bind(this);
        this.onToggle = this.onToggle.bind(this);
        this.onGoToMainContent = this.onGoToMainContent.bind(this);
        this.goToMainContent = this.goToMainContent.bind(this);
        this.getLogoSrc = this.getLogoSrc.bind(this);
    }

    /**
     * Display tasks page
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    onDisplayTasks(event: React.KeyboardEvent): void {
        if (event.key === ENTER_KEY) {
            this.displayTasks();
        }
    }

    /**
     * Display instructions page
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    onDisplayInstructions(event: React.KeyboardEvent): void {
        if (event.key === ENTER_KEY) {
            this.displayInstructions();
        }
    }

    /**
     * Logout
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    onLogout(event: React.KeyboardEvent): void {
        if ([ENTER_KEY, SPACE_KEY].includes(event.key)) {
            this.logout();
        }
    }

    /**
     * Toggle side bar
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    onToggle(event: React.KeyboardEvent): void {
        if ([ENTER_KEY, SPACE_KEY].includes(event.key)) {
            this.props.toggle();
        }
    }

    /**
     * Skip navigation and focus main content
     *
     * @param {React.KeyboardEvent} event
     * @return {void}
     */
    onGoToMainContent(event: React.KeyboardEvent): void {
        if ([ENTER_KEY, SPACE_KEY].includes(event.key)) {
            this.props.focusMainContent();
        }
    }

    getSelectedKey(): string {
        const {contentType}: Props = this.props;
        let selectedKey: string;
        switch (contentType) {
            case ContentType.TASKS:
                selectedKey = Sidebar.tasksKey;
                break;
            case ContentType.INSTRUCTIONS:
                selectedKey = Sidebar.instructionsKey;
                break;
            default:
                selectedKey = Sidebar.tasksKey;
                break;
        }
        return selectedKey;
    }

    /**
     * Gets the proper logo based on environment
     * @param {boolean} collapsedState
     * @param {Environment} env
     * @return {string}
     */
    getLogoSrc(collapsedState: boolean, env: Environment): string {
        let logoSrc: string;
        switch (env) {
            case Environment.DEV:
                logoSrc = collapsedState ? logoDev : typefaceLogoDev;
                break;
            case Environment.STG1:
                logoSrc = collapsedState ? logoStg1 : typefaceLogoStg1;
                break;
            case Environment.STG2:
                logoSrc = collapsedState ? logoStg2 : typefaceLogoStg2;
                break;
            default:
                logoSrc = collapsedState ? logo : typefaceLogo;
                break;
        }
        return logoSrc;
    }

    logout(): void {
        this.props.userManager!.signoutRedirect().catch();
    }

    displayTasks() {
        this.props.setContentType(ContentType.TASKS);
    }

    displayInstructions() {
        this.props.setContentType(ContentType.INSTRUCTIONS);
    }

    /**
     * Skip navigation and focus main content
     *
     * @return {void}
     */
    goToMainContent() {
        this.props.focusMainContent();
    }

    /**
     * Creates custom Sidebar trigger element.
     *
     * @return {React.ReactNode}
     */
    renderTrigger(): React.ReactNode {
        const {collapsed, translate}: Props = this.props;
        const {translationPath}: typeof Sidebar = Sidebar;
        return (
            collapsed
                ? (
                    <RightOutlined
                        tabIndex={0}
                        onKeyDown={this.onToggle}
                        role="button"
                        aria-label={translate(`${translationPath}.expand.text`) as string}
                        className="df-trigger-icon"
                    />
                )
                : (
                    <LeftOutlined
                        tabIndex={0}
                        onKeyDown={this.onToggle}
                        role="button"
                        aria-label={translate(`${translationPath}.collapse.text`) as string}
                        className="df-trigger-icon"
                    />
                )
        );
    }

    render() {
        const {
            collapsed, translate, toggle, env,
        }: Props = this.props;
        const {
            translationPath, tasksKey, instructionsKey, skipLinkKey,
        }: typeof Sidebar = Sidebar;
        const selectedKey: string = this.getSelectedKey();

        return (
            <Layout.Sider
                className="df-sidebar"
                collapsible
                collapsed={collapsed}
                onCollapse={toggle}
                trigger={this.renderTrigger()}
            >
                <div className="df-sidebar-upper">
                    <div className={`df-logo-container ${collapsed ? "collapsed" : "expanded"}`}>
                        <img
                            src={this.getLogoSrc(collapsed, env)}
                            alt={translate(`${translationPath}.logoAltTxt.text`) as string}
                        />
                    </div>
                    <Menu
                        theme="dark"
                        selectedKeys={[selectedKey]}
                        mode="inline"
                        tabIndex={-1}
                    >
                        <Menu.Item
                            key={tasksKey}
                            icon={<ContainerOutlined/>}
                            onClick={this.displayTasks}
                            tabIndex={0}
                            onKeyDown={this.onDisplayTasks}
                            className="tasks-item"
                        >
                            <Translate id={`${translationPath}.tasks.text`}/>
                        </Menu.Item>
                        <Menu.Item
                            key={instructionsKey}
                            icon={<InfoCircleOutlined/>}
                            onClick={this.displayInstructions}
                            tabIndex={0}
                            onKeyDown={this.onDisplayInstructions}
                        >
                            <Translate id={`${translationPath}.instructions.text`}/>
                        </Menu.Item>
                        <Menu.Item
                            className="df-sidebar-skip"
                            key={skipLinkKey}
                            icon={<ForwardOutlined/>}
                            tabIndex={0}
                            onClick={this.goToMainContent}
                            onKeyDown={this.onGoToMainContent}
                        >
                            <Translate id={`${translationPath}.skipToContent.text`}/>
                        </Menu.Item>
                    </Menu>
                </div>

                <Button
                    className="df-logout-btn"
                    type="link"
                    onClick={this.logout}
                    tabIndex={0}
                    onKeyDown={this.onLogout}
                >
                    <LogoutOutlined className="df-logout-btn"/>
                    {!collapsed && (
                        <span>
                            <Translate id={`${translationPath}.logout.text`}/>
                        </span>
                    )}
                </Button>
            </Layout.Sider>
        );
    }
}

function mapStateToProps({
    layout, userManager, localize, serviceProperties,
}: StoreState): StateProps {
    return {
        collapsed: layout.sidebar.collapsed,
        userManager,
        translate: getTranslate(localize),
        contentType: layout.content.contentType,
        env: serviceProperties.env,
    };
}

const mapDispatchToProps: DispatchProps = {
    toggle: toggleSidebar,
    setContentType,
};

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
